import i18next from 'i18next';
import React from 'react';
import { ReactComponent as PendingIcon } from '../../assets/icons/pending.svg';
import { ReactComponent as ProcessingIcon } from '../../assets/icons/processing.svg';
import { ReactComponent as HoldIcon } from '../../assets/icons/hold.svg';
import permissions from '../../config/permissions';
import { CUSTOM_TAGS_OPTIONS } from '../Shared/CustomTags';
import { INFO_PANE_PAGES } from '../../redux_store/reducer/reducers/infoPaneReducer';
import FILTER_TYPES from '../Shared/Table/widgets/Filters/constants';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import MicNoneOutlinedIcon from '@material-ui/icons/MicNoneOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

export const RESOURCE_STATUSES = {
  invited: 'invited',
  engaged: 'engaged',
  scheduled: 'scheduled',
};

export const RESOURCE_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:invited'),
    value: RESOURCE_STATUSES.invited,
    icon: HoldIcon,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:engaged'),
    value: RESOURCE_STATUSES.engaged,
    icon: PendingIcon,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:scheduled'),
    value: RESOURCE_STATUSES.scheduled,
    icon: ProcessingIcon,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
];

export const GET_RESOURCES_COLUMNS = (isPatientPage) => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:dateOfBirth'),
    accessorKey: 'dateOfBirth',
    filter: {
      filterType: FILTER_TYPES.DATE,
    },
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 300,
    header: i18next.t('tables:resource'),
    accessorKey: 'resourceName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:orderTimestamp'),
    accessorKey: 'orderTimestamp',
    shouldRange: true,
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:appointmentTimestamp'),
    accessorKey: 'appointmentTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
      hasNoMaxDate: true,
    },
    isDefaultCell: true,
  },
  {
    size: 220,
    header: i18next.t('tables:invitedTimestamp'),
    accessorKey: 'invitedTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:engagedTimestamp'),
    accessorKey: 'engagedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
      hasNoMaxDate: true,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:createdBy'),
    accessorKey: 'createdBy',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:status'),
    accessorKey: 'status',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: RESOURCE_STATUSES_OPTIONS,
    },
  },
];

function objectMap(object, mapFn) {
  return Object.keys(object).reduce(function(result, key) {
    result[key] = mapFn(object[key]);
    return result;
  }, {});
}

const resourceIcons = {
  article: {
    image: <DescriptionOutlinedIcon style={{
      color: 'rgba(236, 127, 1)',
      width: 24,
      height: 24,
    }} />,
    backgroundColor: 'rgba(236, 127, 1, .1)',
  },
  podcast: {
    image: <MicNoneOutlinedIcon style={{
      color: 'rgba(132, 21, 188)',
      width: 24,
      height: 24,
    }} />,
    backgroundColor: 'rgba(132, 21, 188, .1)',
  },
  video: {
    image: <VideocamOutlinedIcon style={{
      color: 'rgba(249, 88, 135)',
      width: 24,
      height: 24,
    }} />,
    backgroundColor: 'rgba(249, 88, 135, .1)',
  },
};

const resourceIconsForTable = {
  article: {
    image: <DescriptionOutlinedIcon style={{
      color: 'rgba(236, 127, 1)',
      width: 20,
      height: 20,
    }} />,
    backgroundColor: 'rgba(236, 127, 1, .1)',
  },
  podcast: {
    image: <MicNoneOutlinedIcon style={{
      color: 'rgba(132, 21, 188)',
      width: 20,
      height: 20,
    }} />,
    backgroundColor: 'rgba(132, 21, 188, .1)',
  },
  video: {
    image: <VideocamOutlinedIcon style={{
      color: 'rgba(249, 88, 135)',
      width: 20,
      height: 20,
    }} />,
    backgroundColor: 'rgba(249, 88, 135, .1)',
  },
};

export const RESOURCE_ICON_LIST = objectMap(
  resourceIcons,
  ({ backgroundColor, image }) => (
    <div
      style={{
        backgroundColor,
        borderRadius: '50%',
        width: '38px',
        height: '38px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {image}
    </div>
  ),
);

export const RESOURCE_ICON_LIST_FOR_TABLE = objectMap(
  resourceIconsForTable,
  ({ backgroundColor, image }) => (
    <div
      style={{
        backgroundColor,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 28,
        width: 28,
      }}
    >
      {image}
    </div>
  ),
);
